import { PROMOTION_SCHEDULE_LOG_OPERATION, PromotionScheduleLog } from '../../../../lib/api/promotionSchedule';
import { STATUS_LABEL, TRACK_META, TYPE_LABEL } from '../constants';
import { getTimeRangeLabel, toDatePart } from './index';

/** 이력 한 건에 붙는 "무엇이 바뀌었나" 항목. */
export type LogChange = {
  field: string;
  before: string;
  after: string;
};

export type LogEntry = {
  log: PromotionScheduleLog;
  /** `create` 이거나 비교 대상이 없으면 빈 배열 */
  changes: LogChange[];
};

const formatType = (log: PromotionScheduleLog): string => (log.type ? TYPE_LABEL[log.type] : '미지정');

const formatManager = (log: PromotionScheduleLog): string =>
  log.managerAdminName ?? (log.managerAdminId === null ? '미지정' : `#${log.managerAdminId}`);

const formatMemo = (log: PromotionScheduleLog): string => log.memo ?? '(없음)';

/**
 * 스냅샷의 기간은 ISO datetime 이라 그대로 쓰면 UTC 문자열이 노출된다.
 * 종일 항목은 날짜만, 시간 지정 항목은 시각까지 보여준다.
 */
export const formatLogPeriod = (log: PromotionScheduleLog): string => {
  const period = `${toDatePart(log.startDate)} ~ ${toDatePart(log.endDate)}`;
  const timeRangeLabel = getTimeRangeLabel(log.startDate, log.endDate);

  return timeRangeLabel ? `${period} (${timeRangeLabel})` : period;
};

const formatEvent = (log: PromotionScheduleLog): string => (log.eventId === null ? '없음' : `#${log.eventId}`);

/**
 * 연결된 쿠폰은 **id 목록**으로 비교한다.
 *
 * 스냅샷에 이름·할인율을 담지 않는 이유 — 쿠폰이 나중에 수정되면 이력이 그 시점 값과 어긋난다.
 * 이력의 관심사는 "그때 어떤 쿠폰이 걸려 있었나" 이고, 쿠폰 내용의 변천은 쿠폰 이력이 갖는다.
 */
const formatCoupons = (log: PromotionScheduleLog): string => {
  const ids = log.couponIds ?? [];

  // 정렬해서 비교한다 — 같은 연결인데 순서만 다르면 "변경됨" 으로 잡혀 이력이 지저분해진다
  return ids.length
    ? [...ids]
        .sort((a, b) => a - b)
        .map((id) => `#${id}`)
        .join(', ')
    : '없음';
};

/**
 * 비교 대상 필드와 표시 방법.
 * 기간은 시작/종료를 따로 보여주면 "이틀 밀었다"가 안 읽히므로 **한 줄로 묶는다.**
 */
const COMPARERS: { field: string; format: (log: PromotionScheduleLog) => string }[] = [
  { field: '항목명', format: (log) => log.title },
  { field: '기간', format: formatLogPeriod },
  { field: '트랙', format: (log) => TRACK_META[log.track]?.label ?? log.track },
  { field: '상태', format: (log) => STATUS_LABEL[log.status] ?? log.status },
  { field: '기획전 유형', format: formatType },
  { field: '담당자', format: formatManager },
  { field: '연결 기획전', format: formatEvent },
  { field: '연결 쿠폰', format: formatCoupons },
  { field: '메모', format: formatMemo },
];

/**
 * 최신순 이력 배열을 받아 각 건에 변경 내역을 붙인다.
 *
 * `PromotionScheduleUpdateLog` 는 **스냅샷**이라 한 건만 보면 무엇이 바뀌었는지 알 수 없다.
 * 바로 다음(= 시간상 이전) 이력과 비교해서 달라진 필드만 뽑는다.
 * 서버가 아니라 화면에서 계산하는 이유는 어떤 필드를 어떻게 보여줄지가 화면 관심사이기 때문.
 */
export const buildLogEntries = (logs: PromotionScheduleLog[]): LogEntry[] =>
  logs.map((log, index) => {
    // 배열이 최신순이므로 다음 인덱스가 시간상 이전 상태다
    const previous = logs[index + 1];

    if (log.operation === PROMOTION_SCHEDULE_LOG_OPERATION.CREATE || !previous) {
      return { log, changes: [] };
    }

    const changes = COMPARERS.map(({ field, format }) => ({
      field,
      before: format(previous),
      after: format(log),
    })).filter((change) => change.before !== change.after);

    return { log, changes };
  });

export const LOG_OPERATION_LABEL: Record<string, string> = {
  [PROMOTION_SCHEDULE_LOG_OPERATION.CREATE]: '등록',
  [PROMOTION_SCHEDULE_LOG_OPERATION.UPDATE]: '수정',
  [PROMOTION_SCHEDULE_LOG_OPERATION.DELETE]: '삭제',
};
